type HabitaDateInput = Date | string | null | undefined;

export interface HabitaCreationTimeText {
  label: string;
  title: string;
}

const MINUTE_IN_MS = 60 * 1000;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;
const DAY_IN_MS = 24 * HOUR_IN_MS;
const MONTH_IN_DAYS = 30;
const YEAR_IN_DAYS = 365;

function pluralizePtBr(value: number, singular: string, plural: string): string {
  return value === 1 ? singular : plural;
}

function parseHabitaDateInput(value: HabitaDateInput): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const date = new Date(trimmedValue);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatCreationTooltip(date: Date): string {
  const formattedDate = date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const formattedTime = date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `Criado em ${formattedDate} às ${formattedTime}`;
}

export function formatHabitaCreationTime(value: HabitaDateInput, now = new Date()): HabitaCreationTimeText {
  const date = parseHabitaDateInput(value);

  if (!date) {
    return {
      label: 'Criado há -',
      title: 'Data de criação indisponível',
    };
  }

  const diffInMs = Math.max(0, now.getTime() - date.getTime());
  const diffInDays = Math.floor(diffInMs / DAY_IN_MS);
  let amount: number;
  let unit: string;

  if (diffInMs < HOUR_IN_MS) {
    amount = Math.max(1, Math.floor(diffInMs / MINUTE_IN_MS));
    unit = pluralizePtBr(amount, 'minuto', 'minutos');
  } else if (diffInMs < DAY_IN_MS) {
    amount = Math.floor(diffInMs / HOUR_IN_MS);
    unit = pluralizePtBr(amount, 'hora', 'horas');
  } else if (diffInDays < 7) {
    amount = Math.max(1, diffInDays);
    unit = pluralizePtBr(amount, 'dia', 'dias');
  } else if (diffInDays < MONTH_IN_DAYS) {
    amount = Math.floor(diffInDays / 7);
    unit = pluralizePtBr(amount, 'semana', 'semanas');
  } else if (diffInDays < YEAR_IN_DAYS) {
    amount = Math.max(1, Math.floor(diffInDays / MONTH_IN_DAYS));
    unit = pluralizePtBr(amount, 'mês', 'meses');
  } else {
    amount = Math.max(1, Math.floor(diffInDays / YEAR_IN_DAYS));
    unit = pluralizePtBr(amount, 'ano', 'anos');
  }

  return {
    label: `Criado há ${amount} ${unit}`,
    title: formatCreationTooltip(date),
  };
}

export function formatHabitaDate(value: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatHabitaNullableDate(value: string | null | undefined): string {
  if (!value) return '-';

  const brazilianDateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = brazilianDateMatch
    ? new Date(Number(brazilianDateMatch[3]), Number(brazilianDateMatch[2]) - 1, Number(brazilianDateMatch[1]))
    : isoDateMatch
      ? new Date(Number(isoDateMatch[1]), Number(isoDateMatch[2]) - 1, Number(isoDateMatch[3]))
      : new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatHabitaCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';

  return value.toLocaleString('pt-BR', {
    currency: 'BRL',
    style: 'currency',
  });
}

export function formatHabitaShortDateTime(value: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatHabitaDateTime(value: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
